import React, { useState } from "react";
import apiClient from "api/apiClient";
import { Link, useParams, useNavigate } from "react-router-dom";
import "scenes/login/login.css";

import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { token } = useParams();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (password !== confirmPassword) {
      setIsError(true);
      setMessage("Passwords do not match.");
      return;
    }

    try {
      const response = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/user/reset-password`,
        { token, password },
      );
      setIsError(false);
      setMessage(response.data.message);
      navigate("/login");
    } catch (error) {
      setIsError(true);
      setMessage(
        error.response?.data?.message || "An error occurred. Please try again.",
      );
    }
  };

  return (
    <Box className="login-wrapper">
      <section className="login-shell">
        <aside className="login-sidebar">
          <div className="login-sidebar-inner">
            <div className="login-brand-block">
              <div className="login-brand-logo">
                <img src="/images/PGL_Logo-removebg-preview.png" alt="PGL Logo" className="logo" />
              </div>
              <p className="login-brand-eyebrow">Planète GreenLeaves (PGL)</p>
            </div>

            <div className="login-message-block">
              <h1>City-scale climate and energy intelligence for better urban decisions.</h1>
            </div>
          </div>
        </aside>

        <main className="login-main">
          <Paper elevation={6} className="login-container">
            {message ? (
              <Alert severity={isError ? "error" : "success"} sx={{ mb: 2 }}>
                {message}
              </Alert>
            ) : null}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Typography variant="h5" align="center" sx={{ mb: 2, color: "black" }}>
                Reset Password
              </Typography>

              <Stack spacing={2}>
                <TextField
                  id="password"
                  name="password"
                  label="New Password"
                  placeholder="New Password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? "Hide new password" : "Show new password"}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setShowPassword((s) => !s)}
                          edge="end"
                          tabIndex={-1}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  id="confirm-password"
                  name="confirmPassword"
                  label="Confirm New Password"
                  placeholder="Confirm New Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  fullWidth
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setShowConfirmPassword((s) => !s)}
                          edge="end"
                          tabIndex={-1}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button type="submit" variant="contained" size="large" fullWidth>
                  Reset Password
                </Button>

                <Button
                  component={Link}
                  to="/login"
                  variant="outlined"
                  size="large"
                  fullWidth
                  sx={{ textTransform: "none" }}
                >
                  Back to Login
                </Button>
              </Stack>
            </Box>
          </Paper>
        </main>
      </section>
    </Box>
  );
};

export default ResetPassword;
